import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { stripLangPrefix } from '../../i18n/i18n';
import { SLUG_MAP, translatePath, englishToGermanPath } from '../../i18n/slugMap';

const SITE_URL = 'https://monemee.com';

/**
 * SEO Component
 *
 * Wiederverwendbare Komponente zum Setzen von Meta-Tags, Open Graph,
 * Twitter Cards, Canonical-URLs, hreflang-Alternates und JSON-LD.
 *
 * Beispiel:
 * <SEO
 *   title="Mein Produkt"
 *   description="Beschreibung max 160 Zeichen"
 *   canonical="https://monemee.com/p/123"
 *   ogImage="https://monemee.com/og/produkt-123.png"
 *   ogType="product"
 *   structuredData={{ "@context": "https://schema.org", ... }}
 * />
 *
 * Props:
 * - title: Seiten-Titel (wird zu "{title} | Monemee" formatiert; ausser titleTemplate=false)
 * - description: Meta-Description (max 160 Zeichen empfohlen)
 * - canonical: Vollständige kanonische URL (z.B. https://monemee.com/...)
 * - ogImage: Vollständige URL zum Social-Sharing-Bild
 * - ogType: Open-Graph-Typ (default: "website"; z.B. "product", "article")
 * - noIndex: Wenn true, wird Robots-Meta auf "noindex,nofollow" gesetzt
 * - structuredData: Objekt oder Array von Objekten für JSON-LD Script-Tag(s)
 * - titleTemplate: false um Titel-Suffix " | Monemee" zu unterdrücken
 * - alternates: hreflang-Override, z.B. { de: '/pdf-verkaufen', en: '/en/sell-pdf' }.
 *   Ohne Angabe werden Alternates aus der aktuellen Route + slugMap abgeleitet.
 *   alternates={false} unterdrückt hreflang komplett (z.B. Token-Seiten).
 *
 * i18n/hreflang:
 * - Jede indexierbare Seite bekommt de/en/x-default-Alternates, damit Google
 *   beide Sprachversionen als Paar versteht (x-default = deutsche Version).
 * - Landingpages mit eigenen Slugs pro Sprache laufen über i18n/slugMap.js;
 *   solange dort en === null gilt, wird KEIN hreflang ausgegeben (es gibt ja
 *   noch keine englische Entsprechung).
 */
function SEO({
  title,
  description,
  canonical,
  ogImage = 'https://monemee.com/og-default.png',
  ogType = 'website',
  noIndex = false,
  structuredData,
  titleTemplate = true,
  alternates,
}) {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const lang = i18n.language || 'de';

  const fullTitle = title
    ? (titleTemplate ? `${title} | Monemee` : title)
    : t('app.defaultTitle');

  const trimmedDescription = description
    ? description.length > 160
      ? `${description.slice(0, 157)}...`
      : description
    : null;

  const structuredArray = structuredData
    ? Array.isArray(structuredData) ? structuredData : [structuredData]
    : [];

  // --- hreflang-Alternates ableiten ---
  // location.pathname ist dank basename bereits sprachneutral relativ —
  // bei en-Routen mit eigenem Slug (z.B. /help) erst auf den de-Slug mappen.
  let hreflangs = null;
  if (alternates !== false && !noIndex) {
    if (alternates) {
      hreflangs = alternates;
    } else {
      const routePath = stripLangPrefix(location.pathname);
      const neutralPath = lang === 'en' ? englishToGermanPath(routePath) : routePath;
      const slugEntry = SLUG_MAP[neutralPath];
      const hasEnglish = slugEntry === undefined || !!slugEntry.en;
      if (hasEnglish) {
        hreflangs = {
          de: neutralPath,
          en: translatePath(neutralPath, 'en'),
        };
      }
    }
  }

  // Canonical: explizite Prop gewinnt; sonst aus aktueller URL ableiten,
  // damit de- und en-Version je auf sich selbst zeigen.
  const effectiveCanonical = canonical
    || (hreflangs ? `${SITE_URL}${hreflangs[lang] || location.pathname}` : null);

  return (
    <Helmet>
      {/* Title */}
      <title>{fullTitle}</title>

      {/* Description */}
      {trimmedDescription && (
        <meta name="description" content={trimmedDescription} />
      )}

      {/* Canonical */}
      {effectiveCanonical && <link rel="canonical" href={effectiveCanonical} />}

      {/* hreflang — beide Sprachversionen + x-default (de) */}
      {hreflangs && <link rel="alternate" hrefLang="de" href={`${SITE_URL}${hreflangs.de}`} />}
      {hreflangs && <link rel="alternate" hrefLang="en" href={`${SITE_URL}${hreflangs.en}`} />}
      {hreflangs && <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${hreflangs.de}`} />}

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {trimmedDescription && (
        <meta property="og:description" content={trimmedDescription} />
      )}
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content={lang === 'en' ? 'en_US' : 'de_DE'} />
      <meta property="og:site_name" content="MoneMee" />
      {effectiveCanonical && <meta property="og:url" content={effectiveCanonical} />}
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {trimmedDescription && (
        <meta name="twitter:description" content={trimmedDescription} />
      )}
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* JSON-LD Structured Data */}
      {structuredArray.map((data, idx) => (
        <script key={`ld-${idx}`} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

export default SEO;
